import type { AgentDefinition } from '../../../lib/ai/agentDefinitions';
import type { AgentSettings } from '../../../lib/agentSettingsService';

/** What each output kind's review list receives from the generic report page. */
export interface OutputReviewProps {
  def: AgentDefinition;
  config: AgentSettings | null;
  workspaceId: string;
  /** May this person accept or reject this agent's output (its review roles; the database checks the same)? */
  mayReview: boolean;
  /** Tell the page something changed so its counts and records refresh. */
  onChanged: () => void;
  titleOf: (itemId: string) => string;
}

export function when(iso?: string | null): string {
  if (!iso) return '';
  const ms = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(ms / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
}

/** A proposed value in words: a scale value as it is; a classification as "risk · altitude 3". */
export function proposedWords(value: unknown): string {
  if (value && typeof value === 'object') {
    const v = value as { category?: string; altitude_level?: number };
    if (v.category) return `${v.category} · altitude ${v.altitude_level ?? '—'}`;
    return JSON.stringify(value);
  }
  return String(value);
}

/**
 * What a person did with an agent's output, with that person's name (proposal P5:
 * acceptance keeps a person's name). `reviewer` is the decision record's
 * reviewed_by, already turned into a name; for a standing rule it is the
 * administrator who set the rule (migration 920). A reversal names no one here:
 * reviewed_by still holds the person who accepted it.
 */
export function reviewOutcomeWords(outcome: string | null | undefined, reviewer: string | null | undefined): string {
  const by = reviewer ? ` by ${reviewer}` : '';
  switch (outcome) {
    case 'accepted_as_proposed': return `Accepted as proposed${by}`;
    case 'accepted_with_change': return `Accepted with a change${by}`;
    case 'accepted_by_rule': return reviewer ? `Accepted by the standing rule ${reviewer} set` : 'Accepted by the standing rule';
    case 'rejected': return `Rejected${by}`;
    case 'dismissed': return `Dismissed${by}`;
    case 'expired': return 'Expired';
    case 'reversed': return 'Reversed later';
    default: return outcome ?? '';
  }
}
